import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "@/services/api";
import type { SyraUser, UserRole } from "@/types/syra";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Shield, Eye, UserPlus, Pencil, MailCheck, ArrowUpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const roleConfig: Record<UserRole, { label: string; icon: React.ReactNode; color: string }> = {
  superadmin: { label: "Super Admin", icon: <Shield className="w-3 h-3" />, color: "bg-destructive/15 text-destructive border-destructive/30" },
  owner: { label: "Owner", icon: <Shield className="w-3 h-3" />, color: "bg-primary/20 text-primary border-primary/40" },
  admin: { label: "Admin", icon: <Shield className="w-3 h-3" />, color: "bg-primary/15 text-primary border-primary/30" },
  operator: { label: "Operador", icon: <Eye className="w-3 h-3" />, color: "bg-warning/15 text-warning border-warning/30" },
  viewer: { label: "Viewer", icon: <Eye className="w-3 h-3" />, color: "bg-info/20 text-info border-info/30" },
};

export default function UsersAdmin() {
  const { user } = useAuth();
  const role = user?.role;
  const { toast } = useToast();
  const qc = useQueryClient();
  // Single dialog state: null = closed, "create" = new user, SyraUser = editing
  const [dialogState, setDialogState] = useState<null | "create" | SyraUser>(null);

  const dialogOpen = dialogState !== null;
  const isEditing = dialogState !== null && dialogState !== "create";
  const editingUser = isEditing ? (dialogState as SyraUser) : null;

  const closeDialog = () => setDialogState(null);

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: usersApi.list });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => usersApi.toggleActive(id, active),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast({ title: "Usuário atualizado" }); },
  });

  const createMut = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); closeDialog(); toast({ title: "Usuário criado" }); },
    onError: (err: Error) => { toast({ title: "Erro ao criar", description: err.message, variant: "destructive" }); },
  });

  const updateMut = useMutation({
    mutationFn: usersApi.update,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["org-role"] });
      closeDialog();
      toast({ title: "Usuário atualizado" });
    },
    onError: (err: Error) => { toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" }); },
  });

  const confirmEmailMut = useMutation({
    mutationFn: (userId: string) => usersApi.confirmEmail(userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast({ title: "Email confirmado com sucesso" }); },
    onError: (err: Error) => { toast({ title: "Erro ao confirmar email", description: err.message, variant: "destructive" }); },
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => usersApi.delete(userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast({ title: "Usuário apagado com sucesso" }); },
    onError: (err: Error) => { toast({ title: "Erro ao apagar", description: err.message, variant: "destructive" }); },
  });

  if (role !== "superadmin" && role !== "owner" && role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Controle de Usuários</h1>
        <Button onClick={() => setDialogState("create")}><UserPlus className="w-4 h-4 mr-1" /> Novo Usuário</Button>
      </div>

      {isLoading ? <p className="text-muted-foreground">Carregando...</p> : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Papel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(newRole) => {
                          if (newRole !== u.role) {
                            updateMut.mutate({ user_id: u.id, role: newRole });
                          }
                        }}
                      >
                        <SelectTrigger className="w-[130px] h-8 text-xs">
                          <div className="flex items-center gap-1.5">
                            {roleConfig[u.role]?.icon}
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">
                            <span className="flex items-center gap-1.5"><Shield className="w-3 h-3" /> Admin</span>
                          </SelectItem>
                          <SelectItem value="operator">
                            <span className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> Operador</span>
                          </SelectItem>
                          <SelectItem value="viewer">
                            <span className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> Viewer</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.active ? "default" : "secondary"}>{u.active ? "Ativo" : "Inativo"}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString("pt-BR") : "Nunca"}
                    </TableCell>
                    <TableCell>
                      <Switch checked={u.active} onCheckedChange={(checked) => toggleMut.mutate({ id: u.id, active: checked })} />
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setDialogState(u as SyraUser)} title="Editar">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => confirmEmailMut.mutate(u.id)} title="Confirmar email" disabled={confirmEmailMut.isPending}>
                        <MailCheck className="w-4 h-4" />
                      </Button>
                      <ConfirmDeleteDialog itemName={u.name || u.email} itemType="usuário" onConfirm={() => deleteMut.mutate(u.id)} triggerClassName="text-destructive hover:text-destructive" />
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum usuário cadastrado</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
            <DialogDescription>{isEditing ? "Altere o nome ou papel do usuário." : "Preencha os dados para criar um novo usuário."}</DialogDescription>
          </DialogHeader>
          {isEditing && editingUser ? (
            <EditUserForm
              user={editingUser}
              onSubmit={(d) => updateMut.mutate(d)}
              loading={updateMut.isPending}
            />
          ) : (
            <UserForm onSubmit={(d) => createMut.mutate(d)} loading={createMut.isPending} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserForm({ onSubmit, loading }: { onSubmit: (d: { name: string; email: string; password: string; role: string }) => void; loading: boolean }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Nome</label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Email</label>
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Senha</label>
        <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Papel</label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button onClick={() => onSubmit(form)} disabled={loading || !form.name || !form.email || !form.password} className="w-full">
        {loading ? "Criando..." : "Criar Usuário"}
      </Button>
    </div>
  );
}

function EditUserForm({ user, onSubmit, loading }: { user: SyraUser; onSubmit: (d: { user_id: string; name: string; role: string }) => void; loading: boolean }) {
  const [form, setForm] = useState({ name: user.name, role: user.role as string });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Email</label>
        <Input value={user.email} disabled className="opacity-60" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Nome</label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Papel</label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="operator">Operator</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button onClick={() => onSubmit({ user_id: user.id, name: form.name, role: form.role })} disabled={loading || !form.name} className="w-full">
        {loading ? "Salvando..." : "Salvar Alterações"}
      </Button>
    </div>
  );
}
